import { useEffect, useState } from "react";
import { api, API, apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FileText, Download, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const ORIGIN = API.replace(/\/api$/, "");
const TYPE_LABEL = { activation: "Activation", advertiser: "Advertiser onboarding", "advertiser-renewal": "Advertiser renewal", renewal: "Renewal" };

export default function ShopReceiptsDialog({ shopId, shopName }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ftype, setFtype] = useState("all");

  const load = async () => {
    setRows(null);
    try { const { data } = await api.get("/admin/receipts", { params: { shopId } }); setRows(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); setRows([]); }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const ensure = async () => {
    setBusy(true);
    try { await api.get(`/admin/shops/${shopId}/receipt`); await load(); toast.success("Receipt ready"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const exportCsv = async () => {
    try {
      const { data } = await api.get("/admin/receipts/export", { params: { shopId }, responseType: "blob" });
      const href = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = href; a.download = `velopaths-receipts-${shopName || shopId}.csv`; a.click();
      URL.revokeObjectURL(href);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" title="Receipts" data-testid={`shop-receipts-${shopId}`}><FileText className="w-4 h-4" /></Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl" data-testid="shop-receipts-dialog">
        <DialogHeader><DialogTitle>Receipts — {shopName}</DialogTitle></DialogHeader>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={ensure} disabled={busy} data-testid="receipts-generate">{busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}Generate / refresh activation receipt</Button>
          <Button size="sm" variant="outline" onClick={exportCsv} data-testid="receipts-export-csv"><Download className="w-4 h-4 mr-1" />Export CSV</Button>
        </div>
        {rows === null ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : rows.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground" data-testid="receipts-empty">No receipts yet for this shop.</div>
        ) : (
          <>
          <div className="flex flex-wrap gap-1.5">
            {[["all", "All"], ["activation", "Activation"], ["renewal", "Renewal"], ["advertiser", "Advertiser"], ["advertiser-renewal", "Adv. renewal"]].map(([v, l]) => (
              <button key={v} onClick={() => setFtype(v)} className={`px-2.5 py-1 rounded-full text-xs border ${ftype === v ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`} data-testid={`receipts-filter-${v}`}>{l}</button>
            ))}
          </div>
          <div className="rounded-xl border border-border divide-y divide-border max-h-[50vh] overflow-y-auto mt-2">
            {rows.filter((r) => ftype === "all" || r.type === ftype).map((r) => (
              <div key={r.id} className="flex items-center gap-3 p-3" data-testid={`receipt-row-${r.receiptNumber}`}>
                <FileText className="w-4 h-4 text-primary shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{r.receiptNumber} <span className="text-muted-foreground font-normal">· {TYPE_LABEL[r.type] || r.type}</span></div>
                  <div className="text-xs text-muted-foreground truncate">{r.businessName ? `${r.businessName} · ` : ""}${Number(r.total).toFixed(2)} · {r.createdAt ? new Date(r.createdAt).toLocaleDateString() : ""}{r.renewalPeriodStart ? ` · ${r.renewalPeriodStart}→${r.renewalPeriodEnd}` : ""}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => window.open(ORIGIN + r.pdfUrl, "_blank")} data-testid={`receipt-download-${r.receiptNumber}`}><Download className="w-4 h-4 mr-1" />PDF</Button>
              </div>
            ))}
          </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
